import { getMcActiveOrgId, mcGetCloudItem, mcSetCloudItemImmediate } from "./mcCloudSync";
import { recordMcTrafficLogicalSave } from "./mcTrafficProbe";

const QUEUE_PREFIX = "__mc-live-count-save-queue:";
const listeners = new Map();
const inFlight = new Map();
const retryTimers = new Map();
const ackState = new Map();

function queueKey(sessionId = "") {
  return `${QUEUE_PREFIX}${String(sessionId || "").trim()}`;
}

function safeParse(raw) {
  try { return raw ? JSON.parse(raw) : null; } catch (_) { return null; }
}

function readEntry(sessionId) {
  if (typeof window === "undefined" || !sessionId) return null;
  try {
    const entry = safeParse(window.localStorage?.getItem(queueKey(sessionId)));
    return entry?.sessionId === sessionId ? entry : null;
  } catch (_) {
    return null;
  }
}

function writeEntry(entry) {
  if (typeof window === "undefined" || !entry?.sessionId) return;
  try { window.localStorage?.setItem(queueKey(entry.sessionId), JSON.stringify(entry)); } catch (_) {}
}

function removeEntry(sessionId) {
  if (typeof window === "undefined" || !sessionId) return;
  try { window.localStorage?.removeItem(queueKey(sessionId)); } catch (_) {}
}

function emit(sessionId, patch = {}) {
  const current = ackState.get(sessionId) || {};
  const next = { ...current, ...patch, sessionId };
  ackState.set(sessionId, next);
  const subs = listeners.get(sessionId);
  if (subs) subs.forEach((fn) => { try { fn(next); } catch (_) {} });
}

function scheduleRetry(sessionId, delay = 900) {
  if (!sessionId || retryTimers.has(sessionId)) return;
  const timer = setTimeout(() => {
    retryTimers.delete(sessionId);
    processLiveCountSaveQueue(sessionId);
  }, delay);
  retryTimers.set(sessionId, timer);
}

export function countTouchedRows(lines = []) {
  return (Array.isArray(lines) ? lines : []).filter((line) =>
    String(line?.counted ?? "").trim() !== ""
      || String(line?.waste ?? "").trim() !== ""
      || String(line?.note ?? "").trim() !== ""
  ).length;
}

export function getLiveCountSaveQueueStatus(sessionId, fallback = {}) {
  const entry = readEntry(sessionId);
  const ack = ackState.get(sessionId) || {};
  return {
    state: entry || inFlight.has(sessionId) ? "saving" : (ack.state || fallback.state || "saved"),
    savedCount: Number(ack.savedCount ?? fallback.savedCount ?? 0),
    targetCount: Number(entry?.touchedCount ?? ack.targetCount ?? fallback.targetCount ?? 0),
    totalCount: Number(entry?.totalCount ?? ack.totalCount ?? fallback.totalCount ?? 0),
    updatedAt: entry?.updatedAt || ack.updatedAt || fallback.updatedAt || "",
    pending: Boolean(entry || inFlight.has(sessionId)),
  };
}

export function subscribeLiveCountSaveQueue(sessionId, listener) {
  if (!sessionId || typeof listener !== "function") return () => {};
  if (!listeners.has(sessionId)) listeners.set(sessionId, new Set());
  listeners.get(sessionId).add(listener);
  try { listener(getLiveCountSaveQueueStatus(sessionId)); } catch (_) {}
  return () => {
    const subs = listeners.get(sessionId);
    if (!subs) return;
    subs.delete(listener);
    if (!subs.size) listeners.delete(sessionId);
  };
}

export function enqueueLiveCountSave({ sessionId, cloudKey, raw, touchedCount = 0, totalCount = 0, orgId = "" } = {}) {
  if (!sessionId || !cloudKey || !raw) return null;
  const token = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const entry = {
    sessionId,
    cloudKey,
    raw,
    touchedCount: Number(touchedCount || 0),
    totalCount: Number(totalCount || 0),
    orgId: String(orgId || getMcActiveOrgId?.() || "").trim(),
    token,
    updatedAt: new Date().toISOString(),
  };
  writeEntry(entry);
  recordMcTrafficLogicalSave(sessionId, raw, cloudKey);
  emit(sessionId, {
    state: "saving",
    targetCount: entry.touchedCount,
    totalCount: entry.totalCount,
    updatedAt: entry.updatedAt,
  });
  processLiveCountSaveQueue(sessionId);
  return entry;
}

export async function processLiveCountSaveQueue(sessionId) {
  if (!sessionId || inFlight.has(sessionId)) return false;
  const entry = readEntry(sessionId);
  if (!entry) {
    emit(sessionId, { state: "saved" });
    return true;
  }

  const activeOrg = String(getMcActiveOrgId?.() || "").trim();
  if (entry.orgId && activeOrg && entry.orgId !== activeOrg) {
    // Never let a queued draft cross organisation boundaries. It remains durable
    // and resumes when the originating organisation is active again.
    scheduleRetry(sessionId, 1500);
    return false;
  }

  inFlight.set(sessionId, entry.token);
  emit(sessionId, { state: "saving", targetCount: entry.touchedCount, totalCount: entry.totalCount });
  try {
    const ok = await mcSetCloudItemImmediate(entry.cloudKey, entry.raw);
    if (ok !== true) throw new Error("cloud-not-acknowledged");

    const latest = readEntry(sessionId);
    emit(sessionId, {
      savedCount: entry.touchedCount,
      targetCount: latest?.touchedCount ?? entry.touchedCount,
      totalCount: latest?.totalCount ?? entry.totalCount,
      updatedAt: entry.updatedAt,
      state: latest && latest.token !== entry.token ? "saving" : "saved",
    });

    // Remove only the exact revision that was acknowledged. If the operator
    // typed more while this write was in flight, the newer queue entry stays.
    if (!latest || latest.token === entry.token) removeEntry(sessionId);
  } catch (_) {
    emit(sessionId, { state: "saving" });
    scheduleRetry(sessionId, 1200);
    return false;
  } finally {
    inFlight.delete(sessionId);
  }

  if (readEntry(sessionId)) {
    setTimeout(() => processLiveCountSaveQueue(sessionId), 0);
  } else {
    emit(sessionId, { state: "saved" });
  }
  return true;
}


export async function clearLiveCountSaveQueueForTerminalSession(sessionId, { drainMs = 5000 } = {}) {
  const cleanSessionId = String(sessionId || "").trim();
  if (!cleanSessionId) return true;

  // MC427 — once a section is Completed + Verified, finalization is the next
  // authoritative boundary. A stale background autosave must never contend with
  // the finalize transaction forever. First give the queue a short chance to
  // finish normally. If it is still present, confirm Firestore already contains
  // a terminal canonical ledger and then discard only this stale queued revision.
  const drained = await drainLiveCountSaveQueue(cleanSessionId, { timeoutMs: drainMs });
  if (drained) return true;

  const entry = readEntry(cleanSessionId);
  if (!entry?.cloudKey || !/^mc-count-ledger:/i.test(String(entry.cloudKey))) return false;

  try {
    const raw = await mcGetCloudItem(entry.cloudKey);
    const ledger = safeParse(raw);
    const meta = ledger?.meta || {};
    const terminal = Boolean(
      meta.sectionCompletedAt || meta.completedAt ||
      ["completed", "section complete", "section-complete", "finalized"].includes(String(meta.status || "").toLowerCase()) ||
      ["section-complete", "complete", "finalized"].includes(String(meta.lifecycleState || "").toLowerCase())
    );
    if (!terminal) return false;

    removeEntry(cleanSessionId);
    const retry = retryTimers.get(cleanSessionId);
    if (retry) { clearTimeout(retry); retryTimers.delete(cleanSessionId); }
    emit(cleanSessionId, {
      state: "saved",
      savedCount: Number(meta.completedLines || entry.touchedCount || 0),
      targetCount: Number(meta.completedLines || entry.touchedCount || 0),
      totalCount: Number(meta.totalLines || entry.totalCount || 0),
      updatedAt: meta.updatedAt || meta.completedAt || new Date().toISOString(),
      pending: false,
    });
    return true;
  } catch (_) {
    return false;
  }
}

export async function drainLiveCountSaveQueue(sessionId, { timeoutMs = 30000 } = {}) {
  if (!sessionId) return true;
  processLiveCountSaveQueue(sessionId);
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (!readEntry(sessionId) && !inFlight.has(sessionId)) return true;
    await new Promise((resolve) => setTimeout(resolve, 100));
    processLiveCountSaveQueue(sessionId);
  }
  return false;
}

function resumeAllQueuedSaves() {
  if (typeof window === "undefined") return;
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i) || "";
      if (!key.startsWith(QUEUE_PREFIX)) continue;
      const sessionId = key.slice(QUEUE_PREFIX.length);
      if (sessionId) processLiveCountSaveQueue(sessionId);
    }
  } catch (_) {}
}

if (typeof window !== "undefined") {
  setTimeout(resumeAllQueuedSaves, 0);
  window.addEventListener("online", resumeAllQueuedSaves);
  window.addEventListener("mc-cloud-sync-ready", resumeAllQueuedSaves);
}
